import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Upload } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import api from '@/lib/api';

export function IdentityTab({ settings, update, onSave, loading }) {
  const { t } = useI18n();
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (field) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/cms/upload', formData);
        update(field, res.data.url);
        toast.success('Fichier uploade');
      } catch {
        toast.error('Erreur upload');
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  return (
    <div className="space-y-6">
      <Card className="border">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('cms.identity')}</CardTitle>
          <CardDescription>{t('cms.identity_desc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t('cms.site_title')}</Label>
            <Input value={settings.site_title || ''} onChange={e => update('site_title', e.target.value)} data-testid="cms-site-title" />
          </div>
          <div className="space-y-2">
            <Label>{t('cms.site_desc')}</Label>
            <Textarea value={settings.site_description || ''} onChange={e => update('site_description', e.target.value)} rows={2} data-testid="cms-site-desc" />
          </div>
          <div className="space-y-2">
            <Label>{t('cms.logo')}</Label>
            <div className="flex gap-2 items-center">
              <Input value={settings.logo_url || ''} onChange={e => update('logo_url', e.target.value)} placeholder="https://..." className="flex-1" data-testid="cms-logo" />
              <Button variant="outline" size="sm" onClick={() => handleFileUpload('logo_url')} disabled={uploading} data-testid="upload-logo-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              </Button>
            </div>
            {settings.logo_url && <img src={settings.logo_url.startsWith('http') ? settings.logo_url : `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}`} alt="Logo" className="h-12 mt-2 rounded border" />}
          </div>
          <div className="space-y-2">
            <Label>{t('cms.favicon')}</Label>
            <div className="flex gap-2 items-center">
              <Input value={settings.favicon_url || ''} onChange={e => update('favicon_url', e.target.value)} placeholder="https://..." className="flex-1" data-testid="cms-favicon" />
              <Button variant="outline" size="sm" onClick={() => handleFileUpload('favicon_url')} disabled={uploading} data-testid="upload-favicon-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              </Button>
            </div>
            {settings.favicon_url && <img src={settings.favicon_url.startsWith('http') ? settings.favicon_url : `${process.env.REACT_APP_BACKEND_URL}${settings.favicon_url}`} alt="Favicon" className="h-8 mt-2 rounded border" />}
          </div>
        </CardContent>
      </Card>

      <Card className="border">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('cms.hero')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t('cms.hero_title')}</Label>
            <Input value={settings.hero_title || ''} onChange={e => update('hero_title', e.target.value)} data-testid="cms-hero-title" />
          </div>
          <div className="space-y-2">
            <Label>{t('cms.hero_subtitle')}</Label>
            <Textarea value={settings.hero_subtitle || ''} onChange={e => update('hero_subtitle', e.target.value)} rows={2} data-testid="cms-hero-subtitle" />
          </div>
          <div className="space-y-2">
            <Label>{t('cms.hero_image')}</Label>
            <div className="flex gap-2 items-center">
              <Input value={settings.hero_image_url || ''} onChange={e => update('hero_image_url', e.target.value)} className="flex-1" data-testid="cms-hero-image" />
              <Button variant="outline" size="sm" onClick={() => handleFileUpload('hero_image_url')} disabled={uploading}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('cms.about_footer')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t('cms.about_text')}</Label>
            <Textarea value={settings.about_text || ''} onChange={e => update('about_text', e.target.value)} rows={4} data-testid="cms-about" />
          </div>
          <div className="space-y-2">
            <Label>{t('cms.footer_text')}</Label>
            <Input value={settings.footer_text || ''} onChange={e => update('footer_text', e.target.value)} data-testid="cms-footer" />
          </div>
        </CardContent>
      </Card>

      <Card className="border">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('cms.contact')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t('cms.contact_email')}</Label>
            <Input value={settings.contact_email || ''} onChange={e => update('contact_email', e.target.value)} data-testid="cms-contact-email" />
          </div>
          <div className="space-y-2">
            <Label>{t('cms.contact_phone')}</Label>
            <Input value={settings.contact_phone || ''} onChange={e => update('contact_phone', e.target.value)} data-testid="cms-contact-phone" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
